import { backgroundTransitionOnClick, Box, Divider, Empty, Flex, IconFont, Input, Popover, Toast } from '@byecode/ui'
import { ActionHandleEventType, type IndependentAction, type IndependentActionType } from '@lighthouse/core'
import type { FlowActionTypes, FlowNode } from '@lighthouse/shared'
import {
    actionTypeInfos,
    EndNodeType,
    flowActionTypes,
    getIndependentActionsMap,
    NODE_CARD_WIDTH,
    TriggerNodeType,
    useAtomAction,
    useAtomData,
    WorkflowType,
    X_OFFSET
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { clone } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { addWorkflowAtom } from '@/atoms/workflow/action'
import { actionFlowListAtom, automationFlowListAtom } from '@/atoms/workflow/state'
import { workflowLabelMap } from '@/constants/flow'
import { generateNode } from '@/containers/FlowDetail/utils/addNode'
import * as srv from '@/services'
import { getWorkflowRootNode, getWorkflowRunType } from '@/utils'

const SCxListItemWrapper = styled.div`
    overflow-x: hidden;
    overflow-y: auto;

    cursor: pointer;
`

const SCxListItem = styled.div`
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--font-size-normal);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &:not([data-disabled='true']) {
        ${backgroundTransitionOnClick}
    }
`

const SCxListItemLabel = styled.div`
    flex: 1;
`

const SCxListTip = styled.div`
    padding: 8px 16px;
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
`

const SCxSearchWrapper = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--color-white);
    padding: 4px 12px;
`

const SCxGroupLabel = styled.div`
    height: 24px;
    line-height: 24px;
    padding: 0 16px;
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
`

type ActionTypeSelectorItem = {
    value: string
    icon: string
    label: string
    children?: Array<{
        label: string
        value: string
        icon: string
        type?: string
    }>
    divider?: boolean
    color?: string
    flowType?: string
}

interface ListMenuProps {
    item: ActionTypeSelectorItem
    type: string
    typeName?: string
    onCreate?: () => void
    onClick: (value: string) => void
}

const ListMenu: React.FC<ListMenuProps> = ({ item, type, typeName = '动作流', onCreate, onClick }) => {
    const [open, setOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const filteredChildren = useMemo(() => {
        const children = item.children ?? []
        const q = searchValue.trim().toLowerCase()
        if (!q) {
            return children
        }
        return children.filter(child => child.label.toLowerCase().includes(q))
    }, [item.children, searchValue])

    return (
        <Popover
            withinPortal
            width={300}
            trigger="hover"
            position="left-start"
            opened={open}
            onChange={opened => {
                setOpen(opened)
                if (!opened) {
                    setSearchValue('')
                }
            }}
        >
            <Popover.Target>
                <SCxListItem>
                    <IconFont type={item.icon} color={item.color} size={18} />
                    <SCxListItemLabel>{item.label}</SCxListItemLabel>
                    <IconFont type="ArrowRightSmall" color="var(--color-gray-500)" />
                </SCxListItem>
            </Popover.Target>
            <Popover.Dropdown>
                <Box px={16} py={8}>
                    <Input
                        prefix={<IconFont type="Search" color="var(--color-gray-400)" />}
                        value={searchValue}
                        placeholder="搜索"
                        size="md"
                        clearable
                        onClear={() => setSearchValue('')}
                        onChange={e => setSearchValue(e.currentTarget.value)}
                        styles={{ wrapper: { borderRadius: 5 } }}
                    />
                </Box>

                {filteredChildren.length > 0 ? (
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                        {filteredChildren.map(child => (
                            <SCxListItemWrapper key={child.value}>
                                <SCxListItem
                                    onClick={() => {
                                        setOpen(true)
                                        onClick(child.value)
                                    }}
                                >
                                    <IconFont type={child.icon} color="var(--color-gray-400)" size={18} />
                                    {child.label}
                                </SCxListItem>
                            </SCxListItemWrapper>
                        ))}
                    </div>
                ) : (
                    <Empty
                        icon={
                            <IconFont type={type === 'automation' ? 'FlowArrow' : 'MouseClick'} color="var(--color-gray-400)" size={18} />
                        }
                        description="暂无可选的流程"
                        styles={{ root: { height: 100 } }}
                    />
                )}
                <SCxListTip>仅支持选择开始节点是「当动作触发时」的流程</SCxListTip>
                <Divider my={8} color="var(--color-gray-200)" />
                <SCxListItemWrapper>
                    <SCxListItem onClick={onCreate}>
                        <IconFont type="Add" color="var(--color-gray-400)" size={18} />
                        创建{typeName}
                    </SCxListItem>
                </SCxListItemWrapper>
            </Popover.Dropdown>
        </Popover>
    )
}

interface ActionTypeListProps {
    id?: string
    onChange: (value: IndependentAction) => void
    onClose: () => void
    onAfterCreateFlow: (id: string, type: FlowActionTypes) => void
}

export const ActionTypeList: React.FC<ActionTypeListProps> = ({ id: propsId, onChange, onClose, onAfterCreateFlow }) => {
    const { run: addWorkflow } = useAtomAction(addWorkflowAtom)
    const automationWorkflows = useAtomData(automationFlowListAtom)
    const actionWorkflows = useAtomData(actionFlowListAtom)
    const [globalSearchValue, setGlobalSearchValue] = useState('')

    const dynamicActionTypeGroups = useMemo(() => {
        const filterWorkflow = (w: any) => {
            const rootNode = getWorkflowRootNode(w)
            return rootNode?.data.nodeType === 'CLICK_TRIGGER'
        }

        return actionTypeInfos.map(group => {
            return {
                ...group,
                children: group.children.map(item => {
                    if (item.flowType === 'automation') {
                        const children = automationWorkflows.filter(filterWorkflow).map(({ id, type, name }) => ({
                            type,
                            label: name,
                            value: id,
                            icon: 'FlowArrow'
                        }))
                        return { ...item, children }
                    }
                    if (item.flowType === 'action') {
                        const children = actionWorkflows.filter(filterWorkflow).map(({ id, type, name }) => ({
                            type,
                            label: name,
                            value: id,
                            icon: 'MouseClick'
                        }))
                        return { ...item, children }
                    }
                    return item
                })
            }
        })
    }, [actionWorkflows, automationWorkflows])

    const handleFlowCreate = useCallback(
        async (type: WorkflowType) => {
            let flowInitNodes: FlowNode[] = []
            const endNode = generateNode(EndNodeType.END, {
                position: {
                    x: NODE_CARD_WIDTH * 2 + X_OFFSET * 2,
                    y: 0
                }
            })
            switch (type) {
                case WorkflowType.AUTOMATION:
                case WorkflowType.ACTION: {
                    const newNode = generateNode(TriggerNodeType.CLICK_TRIGGER)
                    flowInitNodes = [newNode, endNode]
                    break
                }
                case WorkflowType.SUB_PROCESS: {
                    const newNode = generateNode(TriggerNodeType.ARG_TRIGGER)
                    flowInitNodes = [newNode, endNode]
                    break
                }
                default: {
                    break
                }
            }
            const newWorkflow = await srv.createFlow({
                name: `未命名${workflowLabelMap[type].label}`,
                type,
                runType: getWorkflowRunType(type),
                description: `未命名${workflowLabelMap[type].label}的描述`,
                edges: [],
                nodes: flowInitNodes
            })

            if (newWorkflow) {
                addWorkflow(newWorkflow)
                return newWorkflow.id
            }
        },
        [addWorkflow]
    )

    const assembleActionParams = useCallback(
        (value: string, type?: FlowActionTypes): IndependentAction => {
            const id = propsId || nanoid()
            const buttonActionsMap = getIndependentActionsMap(id)
            if (type && !buttonActionsMap.has(value as IndependentActionType)) {
                return {
                    id,
                    type,
                    data: {
                        [type]: {
                            flowId: value
                        }
                    }
                } as IndependentAction
            }

            const defaultParams = buttonActionsMap.get(value as IndependentActionType)

            return defaultParams ? clone(defaultParams) : (buttonActionsMap.get(ActionHandleEventType.OPEN_PAGE) as IndependentAction)
        },
        [propsId]
    )

    const handleLevel1ItemClick = useCallback(
        (value: string) => {
            onChange(assembleActionParams(value))
            onClose()
        },
        [assembleActionParams, onChange, onClose]
    )

    const handleLevel2ExtraClick = useCallback(
        async (value: string) => {
            const type = value as FlowActionTypes
            if (flowActionTypes.includes(type)) {
                const workflowId = await handleFlowCreate(type === WorkflowType.AUTOMATION ? WorkflowType.AUTOMATION : WorkflowType.ACTION)
                if (!workflowId) {
                    Toast.error('创建流程失败')
                    return
                }
                onAfterCreateFlow(workflowId, type)
            }
        },
        [handleFlowCreate, onAfterCreateFlow]
    )

    const handleLevel2ItemClick = useCallback(
        (value: string, type?: string) => {
            onChange(assembleActionParams(value, type as FlowActionTypes))
            onClose()
        },
        [assembleActionParams, onChange, onClose]
    )

    const globalSearchResults = useMemo(() => {
        const q = globalSearchValue.trim().toLowerCase()
        if (!q) {
            return null
        }

        const results: Array<
            | { kind: 'leaf'; item: ActionTypeSelectorItem }
            | { kind: 'flowChild'; parent: ActionTypeSelectorItem; child: NonNullable<ActionTypeSelectorItem['children']>[number] }
        > = []

        for (const group of dynamicActionTypeGroups) {
            for (const item of group.children as ActionTypeSelectorItem[]) {
                if (item.flowType === 'action' || item.flowType === 'automation') {
                    const children = item.children ?? []
                    for (const child of children) {
                        if (child.label.toLowerCase().includes(q)) {
                            results.push({ kind: 'flowChild', parent: item, child })
                        }
                    }
                    continue
                }

                if (item.label.toLowerCase().includes(q)) {
                    results.push({ kind: 'leaf', item })
                }
            }
        }

        return results
    }, [dynamicActionTypeGroups, globalSearchValue])

    return (
        <Flex direction="column" gap={8}>
            <SCxSearchWrapper>
                <Input
                    prefix={<IconFont type="Search" color="var(--color-gray-400)" />}
                    value={globalSearchValue}
                    placeholder="搜索"
                    size="md"
                    clearable
                    onClear={() => setGlobalSearchValue('')}
                    onChange={e => setGlobalSearchValue(e.currentTarget.value)}
                    styles={{ wrapper: { borderRadius: 5 } }}
                />
            </SCxSearchWrapper>
            <Box>
                {globalSearchResults ? (
                    globalSearchResults.length > 0 ? (
                        globalSearchResults.map(result => {
                            if (result.kind === 'leaf') {
                                return (
                                    <SCxListItemWrapper key={`leaf:${result.item.value}`}>
                                        <SCxListItem onClick={() => handleLevel1ItemClick(result.item.value)}>
                                            <IconFont type={result.item.icon} color={result.item.color} size={18} />
                                            {result.item.label}
                                        </SCxListItem>
                                    </SCxListItemWrapper>
                                )
                            }

                            return (
                                <SCxListItemWrapper key={`flow:${result.parent.value}:${result.child.value}`}>
                                    <SCxListItem onClick={() => handleLevel2ItemClick(result.child.value, result.parent.value)}>
                                        <IconFont type={result.parent.icon} color={result.parent.color} size={18} />
                                        {result.parent.label} &gt; {result.child.label}
                                    </SCxListItem>
                                </SCxListItemWrapper>
                            )
                        })
                    ) : (
                        <Empty
                            icon={<IconFont type="Search" color="var(--color-gray-400)" size={18} />}
                            description="无匹配结果"
                            styles={{ root: { height: 130 } }}
                        />
                    )
                ) : (
                    dynamicActionTypeGroups.map((group, index) => (
                        <div key={group.label}>
                            {index > 0 && <Divider my={8} color="var(--color-gray-200)" />}
                            <SCxGroupLabel>{group.label}</SCxGroupLabel>
                            {group.children.map(item => (
                                <SCxListItemWrapper key={item.value}>
                                    {item.flowType === 'automation' || item.flowType === 'action' ? (
                                        <ListMenu
                                            typeName={item.flowType === 'automation' ? '自动化' : '动作流'}
                                            type={item.flowType}
                                            item={item as ActionTypeSelectorItem}
                                            onClick={v => handleLevel2ItemClick(v, item.value)}
                                            onCreate={() => handleLevel2ExtraClick(item.value)}
                                        />
                                    ) : (
                                        <SCxListItem onClick={() => handleLevel1ItemClick(item.value)}>
                                            <IconFont type={item.icon} color={item.color} size={18} />
                                            {item.label}
                                        </SCxListItem>
                                    )}
                                </SCxListItemWrapper>
                            ))}
                        </div>
                    ))
                )}
            </Box>
        </Flex>
    )
}
